import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { formatAppDate } from '../../services/appConfigRuntime';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { publicAccessApi } from '../../services/fileShareApi';
import { applyDefaultMeasureUnits } from './forgeMeasureUnits';

const PublicViewer = () => {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [viewer, setViewer] = useState(null);
  const viewerRef = useRef(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commenterName, setCommenterName] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentSubmitLoading, setCommentSubmitLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    // Short embeds / client iframes: start collapsed so the model stays usable
    return window.innerHeight >= 520;
  });
  const rootRef = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined' ? 800 : window.innerHeight
  );
  const isCompact = viewportHeight < 520;
  const isVeryShort = viewportHeight < 400;

  const getCommentsHeightBounds = (vh) => {
    const headerChrome = 44;
    const chrome = 36; // title + thin drag handle
    const minViewerStrip = 56;
    const minHeight = 44; // single compose row
    const maxHeight = Math.max(minHeight + 40, vh - headerChrome - chrome - minViewerStrip);
    return { minHeight, maxHeight };
  };

  const getDefaultCommentsHeight = (vh) => {
    const { minHeight, maxHeight } = getCommentsHeightBounds(vh);
    // Tight strip for Name + Comment only; drag up to see the list
    return Math.min(maxHeight, Math.max(minHeight, 48));
  };

  const [commentsPanelHeight, setCommentsPanelHeight] = useState(() => {
    if (typeof window === 'undefined') return 48;
    return getDefaultCommentsHeight(window.innerHeight);
  });
  const [commentsResizing, setCommentsResizing] = useState(false);
  const commentsResizeRef = useRef(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approverName, setApproverName] = useState('');
  const [approveDialogError, setApproveDialogError] = useState('');

  const HEADER_BLUE = '#0078d4';
  const HEADER_BLUE_DARK = '#005a9e';
  const VIEWER_LOGO_SRC = '/pdfjs/images/bldgProj.png';

  // Track embed/container height (iframe or short client page), not only window
  useEffect(() => {
    const el = rootRef.current;
    const update = () => {
      const h = el?.clientHeight || window.innerHeight;
      setViewportHeight(h);
      setCommentsPanelHeight((prev) => {
        const { minHeight, maxHeight } = getCommentsHeightBounds(h);
        return Math.min(maxHeight, Math.max(minHeight, prev));
      });
    };
    setCommentsPanelHeight(getDefaultCommentsHeight(el?.clientHeight || window.innerHeight));
    update();
    window.addEventListener('resize', update);
    let ro;
    if (el && typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, []);

  const commentsTitleDragRef = useRef(null);

  const startCommentsResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!commentsOpen) {
      setCommentsOpen(true);
    }
    if (event.currentTarget.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (_) {
        /* older browsers */
      }
    }
    commentsResizeRef.current = {
      startY: event.clientY,
      startHeight: commentsOpen ? commentsPanelHeight : getCommentsHeightBounds(
        rootRef.current?.clientHeight || window.innerHeight
      ).minHeight,
      pointerId: event.pointerId,
      target: event.currentTarget,
    };
    setCommentsResizing(true);
  };

  /** Title bar: small move = toggle; drag = resize */
  const onCommentsTitlePointerDown = (event) => {
    if (event.button !== 0) return;
    commentsTitleDragRef.current = {
      startY: event.clientY,
      startHeight: commentsPanelHeight,
      moved: false,
      pointerId: event.pointerId,
      target: event.currentTarget,
    };
  };

  const onCommentsTitlePointerMove = (event) => {
    const drag = commentsTitleDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const delta = Math.abs(event.clientY - drag.startY);
    if (!drag.moved && delta < 6) return;
    if (!drag.moved) {
      drag.moved = true;
      if (!commentsOpen) setCommentsOpen(true);
      if (drag.target?.setPointerCapture) {
        try {
          drag.target.setPointerCapture(drag.pointerId);
        } catch (_) {
          /* ignore */
        }
      }
      const startHeight = commentsOpen
        ? drag.startHeight
        : getCommentsHeightBounds(rootRef.current?.clientHeight || window.innerHeight).minHeight;
      commentsResizeRef.current = {
        startY: drag.startY,
        startHeight,
        pointerId: drag.pointerId,
        target: drag.target,
      };
      setCommentsResizing(true);
    }
    // Keep updating height while captured on the title bar (before document listeners attach)
    const resize = commentsResizeRef.current;
    if (resize) {
      const vh = rootRef.current?.clientHeight || window.innerHeight;
      const { minHeight, maxHeight } = getCommentsHeightBounds(vh);
      const nextHeight = resize.startHeight + (resize.startY - event.clientY);
      setCommentsPanelHeight(Math.min(maxHeight, Math.max(minHeight, nextHeight)));
    }
  };

  const onCommentsTitlePointerUp = (event) => {
    const drag = commentsTitleDragRef.current;
    commentsTitleDragRef.current = null;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.moved) {
      // Resize handled by document listeners; swallow click
      event.preventDefault();
      return;
    }
    // Pure click → toggle open/closed
    setCommentsOpen((open) => {
      if (!open) {
        const vh = rootRef.current?.clientHeight || window.innerHeight;
        setCommentsPanelHeight(getDefaultCommentsHeight(vh));
      }
      return !open;
    });
  };

  useEffect(() => {
    if (!commentsResizing) return undefined;

    const handlePointerMove = (event) => {
      const resize = commentsResizeRef.current;
      if (!resize) return;

      const vh = rootRef.current?.clientHeight || window.innerHeight;
      const { minHeight, maxHeight } = getCommentsHeightBounds(vh);
      const nextHeight = resize.startHeight + (resize.startY - event.clientY);
      setCommentsPanelHeight(Math.min(maxHeight, Math.max(minHeight, nextHeight)));
    };

    const stopResize = (event) => {
      const resize = commentsResizeRef.current;
      if (resize?.target?.releasePointerCapture && resize.pointerId != null) {
        try {
          resize.target.releasePointerCapture(resize.pointerId);
        } catch (_) {
          /* ignore */
        }
      }
      commentsResizeRef.current = null;
      setCommentsResizing(false);
      // Snap shut if user dragged the panel almost closed
      setCommentsPanelHeight((h) => {
        const vh = rootRef.current?.clientHeight || window.innerHeight;
        const { minHeight } = getCommentsHeightBounds(vh);
        if (h <= minHeight + 8) {
          setCommentsOpen(false);
        }
        return h;
      });
      if (event) event.preventDefault();
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', stopResize);
    document.addEventListener('pointercancel', stopResize);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', stopResize);
      document.removeEventListener('pointercancel', stopResize);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [commentsResizing]);

  // Load share on mount (link-only, no password)
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!shareId) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }
      try {
        setError(null);
        const verifyResponse = await publicAccessApi.verifyShare(shareId);
        if (!verifyResponse.data.success || cancelled) return;

        const fileInfoResponse = await publicAccessApi.getFileInfo(shareId);
        if (!fileInfoResponse.data.success || cancelled) {
          setError(fileInfoResponse.data.message || 'Failed to load file info');
          return;
        }
        setFileInfo(fileInfoResponse.data.data);
        // Forge viewer is loaded in a separate effect after the viewer container is in the DOM
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading public share:', err);
          setError(err.response?.data?.message || 'Failed to load file');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [shareId]);

  // Load Forge viewer only after fileInfo is set and the viewer container has been rendered (so viewerRef.current exists)
  useEffect(() => {
    if (!fileInfo || !shareId) return;
    const fileType = fileInfo.fileType;
    if (fileType !== 'RVT' && fileType !== 'DWG') return;

    let cancelled = false;

    const loadForgeViewer = async () => {
      try {
        const configResponse = await publicAccessApi.getViewerConfig(shareId);
        if (!configResponse.data.success || cancelled) {
          if (!cancelled) setError('Failed to load 3D viewer');
          return;
        }
        const config = configResponse.data.data;

        if (!window.Autodesk) {
          await loadForgeViewerScript();
        }
        if (cancelled) return;
        await initializeForgeViewer(config);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading Forge viewer:', err);
          setError('Failed to load 3D viewer');
        }
      }
    };

    loadForgeViewer();
    return () => { cancelled = true; };
  }, [fileInfo, shareId]);

  const loadForgeViewerScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Autodesk) {
        resolve();
        return;
      }

      // Load CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css';
      document.head.appendChild(link);

      // Load JavaScript
      const script = document.createElement('script');
      script.src = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Forge Viewer script'));
      document.head.appendChild(script);
    });
  };

  const initializeForgeViewer = async (config) => {
    return new Promise((resolve, reject) => {
      try {
        const options = {
          env: 'AutodeskProduction',
          api: 'derivativeV2',
          getAccessToken: (onTokenReady) => {
            onTokenReady(config.accessToken, 3600);
          }
        };

        window.Autodesk.Viewing.Initializer(options, () => {
          const viewerContainer = viewerRef.current;
          if (!viewerContainer) {
            reject(new Error('Viewer container not found'));
            return;
          }

          const viewerInstance = new window.Autodesk.Viewing.GuiViewer3D(viewerContainer);
          viewerInstance.start();

          const documentId = `urn:${config.forgeUrn}`;
          window.Autodesk.Viewing.Document.load(documentId, (doc) => {
            try {
              const viewables = doc.getRoot().getDefaultGeometry();
              if (viewables) {
                viewerInstance.loadDocumentNode(doc, viewables).then(() => {
                  console.log('Model loaded successfully');
                  // Client default: feet + fractional inches (e.g. 6' - 8")
                  applyDefaultMeasureUnits(viewerInstance);
                  setViewer(viewerInstance);
                  resolve();
                }).catch((loadErr) => {
                  console.error('Error loading document node:', loadErr);
                  reject(loadErr);
                });
              } else {
                reject(new Error('No viewable geometry found'));
              }
            } catch (parseErr) {
              console.error('Error parsing Forge document:', parseErr);
              reject(parseErr);
            }
          }, (errorCode) => {
            console.error('Error loading Forge document:', errorCode);
            reject(new Error(`Forge document load failed (code ${errorCode})`));
          });
        });
      } catch (err) {
        console.error('Error initializing viewer:', err);
        reject(err);
      }
    });
  };

  const fetchComments = async () => {
    if (!shareId) return;
    try {
      setCommentsLoading(true);
      const res = await publicAccessApi.getComments(shareId);
      if (res.data.success && res.data.data) {
        const list = Array.isArray(res.data.data) ? [...res.data.data] : [];
        // Newest first (YouTube-style)
        list.sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return tb - ta;
        });
        setComments(list);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  };

  useEffect(() => {
    if (fileInfo && shareId) {
      fetchComments();
    }
  }, [fileInfo, shareId]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !commenterName.trim()) return;
    try {
      setCommentSubmitLoading(true);
      await publicAccessApi.addComment(shareId, newComment.trim(), commenterName.trim());
      setNewComment('');
      setCommenterName('');
      await fetchComments();
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(err.response?.data?.message || 'Failed to add comment');
    } finally {
      setCommentSubmitLoading(false);
    }
  };

  const openApproveDialog = () => {
    setApproveDialogError('');
    // Prefill from comment Name if present — approver can still change it
    setApproverName(commenterName.trim() || '');
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!approverName.trim()) {
      setApproveDialogError('Please enter your name to approve this file.');
      return;
    }
    try {
      setApproveLoading(true);
      setApproveDialogError('');
      await publicAccessApi.approve(shareId, approverName.trim());
      const fileInfoResponse = await publicAccessApi.getFileInfo(shareId);
      if (fileInfoResponse.data.success) {
        setFileInfo(fileInfoResponse.data.data);
      }
      setApproveDialogOpen(false);
      setApproverName('');
    } catch (err) {
      console.error('Error approving:', err);
      setApproveDialogError(err.response?.data?.message || 'Failed to approve');
    } finally {
      setApproveLoading(false);
    }
  };

  const formatApprovalStatus = (info) => {
    if (!info?.clientApprovedAt) return null;
    const dateStr = formatAppDate(info.clientApprovedAt);
    if (info.clientApprovedBy) {
      return `Approved by ${info.clientApprovedBy} on ${dateStr}`;
    }
    return `Approved on ${dateStr}`;
  };

  const handleDownload = async () => {
    try {
      const response = await publicAccessApi.downloadFile(shareId);
      
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileInfo.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError(err.response?.data?.message || 'Download failed');
    }
  };

  useEffect(() => {
    return () => {
      if (viewer && typeof viewer.finish === 'function') {
        try {
          if (typeof viewer.__asrosyncMeasureUnitsCleanup === 'function') {
            viewer.__asrosyncMeasureUnitsCleanup();
          }
          viewer.finish();
        } catch (err) {
          // Ignore Forge internal errors on teardown (can happen during hot reloads / unmount)
          console.warn('Error while finishing Forge viewer during unmount:', err);
        }
      }
    };
  }, [viewer]);

  // Track container size instead of a fixed delay: the comments Collapse animates for ~300ms,
  // so a single early resize leaves the canvas at its old height.
  useEffect(() => {
    const container = viewerRef.current;
    if (!viewer || !container) return undefined;

    const resizeViewer = () => {
      try {
        if (typeof viewer.resize === 'function') {
          viewer.resize();
        }
      } catch (err) {
        console.warn('Forge viewer resize after layout change:', err);
      }
    };

    if (typeof window.ResizeObserver !== 'function') {
      const id = window.setTimeout(resizeViewer, 400);
      return () => window.clearTimeout(id);
    }

    let frame = null;
    const observer = new window.ResizeObserver(() => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(resizeViewer);
    });
    observer.observe(container);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [commentsOpen, viewer]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6">Loading file...</Typography>
        </Paper>
      </Box>
    );
  }

  if (error && !fileInfo) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The link may be invalid, expired, or revoked.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      ref={rootRef}
      sx={{
        // 100dvh = real visible height (short browser / iframe embed)
        height: '100dvh',
        maxHeight: '100dvh',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        '@supports not (height: 100dvh)': {
          height: '100vh',
          maxHeight: '100vh',
        },
      }}
    >
      {/* Header — filename only, no colored bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: isCompact ? 1 : 2,
          py: isVeryShort ? 0.5 : 1,
          backgroundColor: 'transparent',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            flexGrow: 1,
            color: '#000000',
            fontSize: isVeryShort ? '0.75rem' : '0.875rem',
            fontWeight: 500,
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fileInfo?.fileName || 'File Viewer'}
        </Typography>
        <Box
          component="img"
          src={VIEWER_LOGO_SRC}
          alt="BLDG PROJ"
          sx={{
            height: isVeryShort ? 24 : isCompact ? 28 : 36,
            width: 'auto',
            ml: 1,
            flexShrink: 0,
          }}
        />
        {fileInfo?.allowDownload && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              ml: 1,
              flexShrink: 0,
              fontSize: isCompact ? '0.7rem' : undefined,
              py: isVeryShort ? 0.25 : undefined,
            }}
          >
            Download
          </Button>
        )}
      </Box>

      {/* Viewer Container */}
      <Box sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
          {fileInfo?.fileType === 'RVT' || fileInfo?.fileType === 'DWG' ? (
            <Box
              ref={viewerRef}
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  File Preview Not Available
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  This file type cannot be previewed in the browser.
                </Typography>
                {fileInfo?.allowDownload && (
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                  >
                    Download File
                  </Button>
                )}
              </Paper>
            </Box>
          )}
        </Box>

        {/* Comments panel — bottom; drag handle freely resizes up/down */}
        <Paper
          elevation={2}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: '#000000',
            color: '#ffffff',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            zIndex: 2,
            // No % maxHeight cap — height comes from drag (commentsPanelHeight)
          }}
        >
          {(commentsOpen || commentsResizing) && (
            <Box
              role="separator"
              aria-label="Drag to resize comments panel"
              title="Drag up or down to resize"
              onPointerDown={startCommentsResize}
              sx={{
                height: 10,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'ns-resize',
                touchAction: 'none',
                backgroundColor: commentsResizing ? '#111111' : '#000000',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: -8,
                  bottom: -2,
                },
                '&::after': {
                  content: '""',
                  width: 48,
                  height: 3,
                  borderRadius: 2,
                  backgroundColor: commentsResizing ? HEADER_BLUE : '#888888',
                },
                '&:hover::after': {
                  backgroundColor: HEADER_BLUE,
                },
              }}
            />
          )}
          <Box
            onPointerDown={onCommentsTitlePointerDown}
            onPointerMove={onCommentsTitlePointerMove}
            onPointerUp={onCommentsTitlePointerUp}
            onPointerCancel={() => {
              commentsTitleDragRef.current = null;
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              cursor: commentsOpen ? 'ns-resize' : 'pointer',
              touchAction: 'none',
              backgroundColor: '#000000',
              '&:hover': { backgroundColor: '#111111' },
            }}
          >
            <Button
              fullWidth
              disableRipple
              tabIndex={-1}
              startIcon={<CommentIcon sx={{ fontSize: isCompact ? 16 : 18 }} />}
              endIcon={commentsOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                backgroundColor: 'transparent',
                color: '#ffffff',
                flexShrink: 0,
                minHeight: 28,
                py: 0,
                px: 1.25,
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1,
                pointerEvents: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '& .MuiButton-startIcon': { mr: 0.75 },
                '& .MuiButton-endIcon': { ml: 0.5 },
              }}
            >
              Comments {comments.length > 0 && `(${comments.length})`}
              {commentsOpen && (
                <Typography
                  component="span"
                  sx={{
                    ml: 'auto',
                    mr: 0.5,
                    fontSize: '0.65rem',
                    color: '#888888',
                    textTransform: 'none',
                    fontWeight: 400,
                    display: { xs: 'none', sm: 'inline' },
                  }}
                >
                  drag up / down
                </Typography>
              )}
            </Button>
          </Box>
          <Collapse in={commentsOpen} timeout={0} unmountOnExit sx={{ minHeight: 0, overflow: 'hidden' }}>
            <Box
              sx={{
                px: 1.25,
                pt: 0.25,
                pb: 0.75,
                boxSizing: 'border-box',
                color: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                gap: 0.75,
                minHeight: commentsPanelHeight,
                // Compact: grow with multiline comment; dragged taller: fixed height + scroll list
                height: commentsPanelHeight > 72 ? commentsPanelHeight : 'auto',
                maxHeight: commentsPanelHeight > 72 ? commentsPanelHeight : undefined,
                overflow: 'hidden',
                fontSize: '0.8125rem',
              }}
            >
              {commentsLoading ? (
                <Box display="flex" justifyContent="center" py={1}>
                  <CircularProgress size={22} />
                </Box>
              ) : (
                <>
                  <Box
                    component="form"
                    onSubmit={handleAddComment}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: newComment.includes('\n') ? 'flex-start' : 'center',
                      gap: 1,
                      flexShrink: 0,
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                      minHeight: 36,
                    }}
                  >
                      <TextField
                        size="small"
                        margin="none"
                        placeholder="Name *"
                        value={commenterName}
                        onChange={(e) => setCommenterName(e.target.value)}
                        disabled={commentSubmitLoading}
                        required
                        inputProps={{ maxLength: 255, 'aria-label': 'Name' }}
                        sx={{
                          m: 0,
                          width: { xs: '100%', sm: 140 },
                          flexShrink: 0,
                          height: 36,
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            backgroundColor: '#1a1a1a',
                            fontSize: '0.8125rem',
                            minHeight: '36px !important',
                            height: '36px !important',
                            borderRadius: 1,
                            boxSizing: 'border-box',
                            padding: '0 12px !important',
                            alignItems: 'center',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3a3a3a',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#555555',
                          },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: HEADER_BLUE,
                          },
                          '& .MuiOutlinedInput-input': {
                            padding: '0 !important',
                            height: 20,
                            lineHeight: '20px',
                            boxSizing: 'border-box',
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#888888',
                            opacity: 1,
                          },
                        }}
                      />
                      <TextField
                        size="small"
                        margin="none"
                        multiline
                        minRows={1}
                        maxRows={4}
                        placeholder="Comment *"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        disabled={commentSubmitLoading}
                        required
                        inputProps={{ 'aria-label': 'Comment' }}
                        sx={{
                          m: 0,
                          flex: 1,
                          flexBasis: { xs: '100%', sm: 0 },
                          minWidth: 0,
                          height: newComment.includes('\n') ? 'auto' : 36,
                          display: 'flex',
                          alignItems: 'center',
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            backgroundColor: '#1a1a1a',
                            fontSize: '0.8125rem',
                            borderRadius: 1,
                            boxSizing: 'border-box',
                            width: '100%',
                            padding: newComment.includes('\n')
                              ? '6px 12px !important'
                              : '0 12px !important',
                            minHeight: '36px !important',
                            height: newComment.includes('\n') ? 'auto' : '36px !important',
                            maxHeight: newComment.includes('\n') ? 'none' : '36px !important',
                            alignItems: newComment.includes('\n') ? 'flex-start' : 'center',
                            overflow: 'hidden',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3a3a3a',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#555555',
                          },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: HEADER_BLUE,
                          },
                          '& textarea': {
                            padding: '0 !important',
                            margin: 0,
                            lineHeight: '20px !important',
                            fontSize: '0.8125rem',
                            minHeight: '20px !important',
                            height: newComment.includes('\n') ? 'auto' : '20px !important',
                            maxHeight: newComment.includes('\n') ? 'none' : '20px !important',
                            overflow: newComment.includes('\n') ? 'auto' : 'hidden',
                            resize: 'none',
                            boxSizing: 'border-box',
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#888888',
                            opacity: 1,
                          },
                        }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        size="small"
                        disabled={commentSubmitLoading || !newComment.trim() || !commenterName.trim()}
                        sx={{
                          flexShrink: 0,
                          flexGrow: { xs: 1, sm: 0 },
                          alignSelf: { xs: 'stretch', sm: newComment.includes('\n') ? 'flex-start' : 'center' },
                          height: '36px !important',
                          minHeight: '36px !important',
                          maxHeight: '36px !important',
                          minWidth: 104,
                          px: 1.5,
                          py: 0,
                          lineHeight: 1,
                          backgroundColor: HEADER_BLUE,
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          fontWeight: 600,
                          boxSizing: 'border-box',
                          '&.Mui-disabled': {
                            backgroundColor: HEADER_BLUE,
                            color: '#ffffff',
                            opacity: 0.45,
                          },
                          '&:hover': {
                            backgroundColor: HEADER_BLUE_DARK,
                          },
                        }}
                      >
                        {commentSubmitLoading ? '…' : 'Add comment'}
                      </Button>
                      {!fileInfo?.clientApprovedAt && (
                        <Button
                          type="button"
                          variant="outlined"
                          size="small"
                          disabled={approveLoading}
                          onClick={() => openApproveDialog()}
                          sx={{
                            flexShrink: 0,
                            // Approval is the point of a public share: keep reachable at every width
                            flexGrow: { xs: 1, sm: 0 },
                            alignSelf: { xs: 'stretch', sm: newComment.includes('\n') ? 'flex-start' : 'center' },
                            height: '36px !important',
                            minHeight: '36px !important',
                            maxHeight: '36px !important',
                            minWidth: 104,
                            px: 1.25,
                            py: 0,
                            lineHeight: 1,
                            borderColor: '#444',
                            color: '#ddd',
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            boxSizing: 'border-box',
                            display: 'inline-flex',
                            '&:hover': {
                              borderColor: HEADER_BLUE,
                              backgroundColor: 'rgba(0,120,212,0.12)',
                            },
                          }}
                        >
                          {approveLoading ? '…' : 'Approve'}
                        </Button>
                      )}
                      {fileInfo?.clientApprovedAt && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#9acd9a',
                            display: 'flex',
                            width: { xs: '100%', sm: 'auto' },
                            whiteSpace: { xs: 'normal', sm: 'nowrap' },
                            alignSelf: newComment.includes('\n') ? 'flex-start' : 'center',
                            alignItems: 'center',
                            minHeight: 36,
                            lineHeight: 1.3,
                          }}
                        >
                          {formatApprovalStatus(fileInfo)}
                        </Typography>
                      )}
                  </Box>

                  {/* Existing comments — only when panel is tall enough */}
                  {commentsPanelHeight > 72 && (
                  <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                    {comments.length === 0 ? (
                      <Typography
                        variant="caption"
                        sx={{ color: '#777777' }}
                      >
                        No comments yet. Drag up for more space.
                      </Typography>
                    ) : (
                      <List dense disablePadding>
                        {comments.map((c) => (
                          <ListItem key={c.commentId} alignItems="flex-start" disablePadding sx={{ mb: 0.75 }}>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#ffffff',
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                    fontSize: '0.8rem',
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {c.text}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    color: '#888888',
                                    fontSize: '0.7rem',
                                  }}
                                >
                                  {c.authorDisplayName || c.authorType} · {c.createdAt ? new Date(c.createdAt).toLocaleString() : ''}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* Approver name — separate from comment Name (approver may be a different person) */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !approveLoading && setApproveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 10000 }}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
            border: '1px solid #333333',
          },
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', pb: 0 }}>Approve this file</DialogTitle>
        {/* pt override: MUI zeroes padding under DialogTitle and clips the outlined label */}
        <DialogContent sx={{ pt: '20px !important', pb: 1, overflow: 'visible' }}>
          {approveDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {approveDialogError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Your name"
            value={approverName}
            onChange={(e) => setApproverName(e.target.value)}
            disabled={approveLoading}
            inputProps={{ maxLength: 255 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleApproveConfirm();
              }
            }}
            sx={{
              mt: 0.5,
              '& .MuiInputLabel-root': { color: '#bbbbbb' },
              '& .MuiInputLabel-root.Mui-focused': { color: HEADER_BLUE },
              '& .MuiInputBase-root': {
                color: '#ffffff',
                backgroundColor: '#111111',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#444444',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#666666',
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: HEADER_BLUE,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pt: 1, pb: 2 }}>
          <Button
            onClick={() => setApproveDialogOpen(false)}
            disabled={approveLoading}
            sx={{ color: '#bbbbbb', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApproveConfirm}
            disabled={approveLoading || !approverName.trim()}
            startIcon={approveLoading ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : <CheckCircleIcon />}
            sx={{
              textTransform: 'none',
              backgroundColor: HEADER_BLUE,
              color: '#ffffff',
              '&:hover': { backgroundColor: HEADER_BLUE_DARK },
              '&.Mui-disabled': {
                backgroundColor: HEADER_BLUE,
                color: '#ffffff',
                opacity: 0.5,
              },
            }}
          >
            {approveLoading ? 'Approving...' : 'Confirm approval'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublicViewer;

